import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faCopy } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";

const ACTION_BUTTON =
	"inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-border-light bg-white px-3 text-xs font-semibold text-text hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Sticky bulk action bar of the Campaigns overview selection (PREC-3880): Duplicate over the
 * selected campaigns. Campaign-level (not list-level), so it is a page-owned bar rather than the
 * shared Table footer (whose functions are list export/archive/delete).
 *
 * Resume / Pause were removed here (PREC-4129) — see {@link useCampaignBulkActions} for why. A
 * campaign is started or paused from its own pill, which acts on exactly that campaign.
 */
export const CampaignsBulkBar: FunctionComponent<{
	selectedCount: number;
	isPending: boolean;
	onDuplicate: () => void;
	onClear: () => void;
}> = ({ selectedCount, isPending, onDuplicate, onClear }) => {
	const { t } = useTranslation();

	if (selectedCount === 0) {
		return null;
	}

	return (
		<div className="sticky bottom-0 z-20 flex h-14 flex-shrink-0 items-center gap-3 border-t border-border-light bg-white px-4 shadow-[0_-3px_12px_rgba(16,24,40,0.06)]">
			<span className="text-sm font-semibold">
				{t("campaignsOverview.bulk.selected", { count: selectedCount })}
			</span>
			<button
				className="cursor-pointer text-sm font-medium text-brand-light hover:underline"
				type="button"
				onClick={onClear}
			>
				{t("campaignsOverview.bulk.clear")}
			</button>
			<div className="flex-1" />
			{isPending && <Spinner color="#4d66f5" />}
			<button className={ACTION_BUTTON} disabled={isPending} type="button" onClick={onDuplicate}>
				<Icon className="h-3 w-3" icon={faCopy} />
				{t("campaignsOverview.bulk.duplicate")}
			</button>
		</div>
	);
};
